import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Form,
  Input,
  Button,
  Space,
  Alert,
  Spin,
  Select,
  Row,
  Col,
  Card,
  DatePicker,
  Divider,
} from 'antd';
import { supabase } from '../supabase/client';
import ChatBox from '../components/ChatBox';
import moment from 'moment';
import {
  getProjects,
  createProject,
  updateProject,
  addProjectComment,
} from '../services/projectsService';
import { getContacts } from '../services/contactsService';
import { getSettings } from '../services/settingsService';

const { Title, Text } = Typography;
const { TextArea } = Input;

function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [projectsLabel, setProjectsLabel] = useState('Projects');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactsData, settingsData] = await Promise.all([getContacts(), getSettings()]);
        setContacts(contactsData);

        // Parse settings
        const types = settingsData.find(s => s.key === 'project_types')?.value || [];
        const statuses = settingsData.find(s => s.key === 'project_statuses')?.value || [];
        const categories = settingsData.find(s => s.key === 'project_categories')?.value || [];

        const projectsLabelData = settingsData.find(setting => setting.key === 'projects_title');
        setProjectsLabel(projectsLabelData.value || 'Project');

        setProjectTypes(types);
        setProjectStatuses(statuses);
        setProjectCategories(categories);

        if (id) {
          const projectsData = await getProjects();
          const foundProject = projectsData.find(p => p.id === id);
          if (foundProject) {
            setProject(foundProject);
            form.setFieldsValue({
              title: foundProject.title,
              description: foundProject.description || '',
              status: foundProject.status,
              type: foundProject.type,
              billing_type: foundProject.billing_type,
              budget: foundProject.budget,
              start_date: foundProject.start_date ? moment(foundProject.start_date) : null,
              end_date: foundProject.end_date ? moment(foundProject.end_date) : null,
              contact_ids: foundProject.project_contacts?.map(pc => pc.contact.id) || [],
            });
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, form]);

  const fetchProject = async () => {
    try {
      const projectsData = await getProjects();
      const foundProject = projectsData.find(p => p.id === id);
      if (foundProject) {
        setProject(foundProject);
        form.setFieldsValue({
          title: foundProject.title,
          status: foundProject.status,
          start_date: foundProject.start_date ? moment(foundProject.start_date) : null,
          end_date: foundProject.end_date ? moment(foundProject.end_date) : null,
          category: foundProject.category,
          email: foundProject.email,
          phone: foundProject.phone,
          phone_ext: foundProject.phone_ext,
          website: foundProject.website,
          details: foundProject.details,
          address1: foundProject.address1,
          address2: foundProject.address2,
          city: foundProject.city,
          state: foundProject.state,
          country: foundProject.country,
          zip: foundProject.zip,
          location_phone: foundProject.location_phone,
          location_phone_ext: foundProject.location_phone_ext,
          contact_ids: foundProject.project_contacts?.map(pc => pc.contact.id) || [],
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addProjectComment(id, newComment);
      setNewComment('');
      fetchProject();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async values => {
    try {
      if (id) {
        await updateProject(id, values);
      } else {
        await createProject(values);
      }
      navigate('/admin/projects');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={2}>{id ? 'Edit' : 'New'}</Title>
          <Button onClick={() => navigate('/admin/projects')}>Back to {projectsLabel}</Button>
        </Space>
      </div>

      <Row gutter={24}>
        <Col span={16}>
          <Card>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Please enter title' }]}
              >
                <Input />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'Please select status' }]}
                  >
                    <Select>
                      {projectStatuses.map(status => (
                        <Select.Option key={status} value={status}>
                          {status}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="start_date" label="Start Date">
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="end_date" label="End Date">
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="type"
                    label="Type"
                    rules={[{ required: true, message: 'Please select type' }]}
                  >
                    <Select>
                      {projectTypes.map(type => (
                        <Select.Option key={type} value={type}>
                          {type}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="category" label="Category">
                    <Select>
                      {projectCategories.map(category => (
                        <Select.Option key={category} value={category}>
                          {category}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="email" label="Email">
                    <Input type="email" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="website" label="Website">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="phone" label="Phone">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="phone_ext" label="Phone Extension">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="details" label="Details">
                <TextArea rows={4} />
              </Form.Item>

              <Divider orientation="left">Location</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="address1" label="Address 1">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="address2" label="Address 2">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item name="city" label="City">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="state" label="State">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="country" label="Country">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="zip" label="ZIP">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="location_phone" label="Location Phone">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="location_phone_ext" label="Location Phone Extension">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="contact_ids" label="Contacts">
                <Select mode="multiple" placeholder="Select contacts" optionFilterProp="children">
                  {contacts.map(contact => (
                    <Select.Option key={contact.id} value={contact.id}>
                      {`${contact.first_name} ${contact.last_name}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => navigate('/admin/projects')}>Cancel</Button>
                  <Button type="primary" htmlType="submit">
                    {id ? 'Update' : 'Create'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        {id && (
          <Col span={8}>
            <Card title="Comments">
              <div style={{ marginBottom: 16 }}>
                <TextArea
                  rows={4}
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                />
                <Button
                  type="primary"
                  onClick={handleAddComment}
                  style={{ marginTop: 8 }}
                  disabled={!newComment.trim()}
                >
                  Add Comment
                </Button>
              </div>
              <Divider />
              <ChatBox
                comments={project?.project_comments?.sort(
                  (a, b) => new Date(a.created_at) - new Date(b.created_at)
                )}
                currentUserId={(async () => {
                  const {
                    data: { user },
                  } = await supabase.auth.getUser();
                  return user?.id;
                })()}
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}

export default ProjectPage;
